"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RelatedPostsList({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="5960" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 w-full animate-pulse rounded-none bg-[#A7F3D0]/30" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  
  const filteredPosts = posts.filter((p) => p.id !== currentPostId).slice(0, 3);

  if (filteredPosts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {filteredPosts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-[#0D9488]/10 bg-[#A7F3D0] rounded-none transition-all hover:shadow-sm">
          <div className="relative aspect-video w-full overflow-hidden bg-[#FFF7ED]">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#FFF7ED] text-[#333333]/50">
                <span className="text-sm font-medium">No Image</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs text-[#333333]/70">
              <Calendar className="size-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold leading-tight text-[#0D9488] group-hover:text-[#FF6B35]">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="grow p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm text-[#333333]">
              <div dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" size="sm" className="w-full justify-between border-[#FF6B35] text-[#FF6B35] rounded-none hover:bg-[#D8A7CA] hover:text-white transition-colors">
                Read More <ArrowRight className="size-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId, categoryIds }: { currentPostId: string; categoryIds?: number[] }) {
  
  const relatedQueryArgs: any = {
    posts_per_page: 3,
    post__not_in: [currentPostId],
    orderby: "date",
    order: "DESC",
    post_type: "post",
  };

  
  if (categoryIds && categoryIds.length > 0) {
    relatedQueryArgs.category__in = categoryIds;
  }

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between border-b border-[#0D9488]/10 pb-4">
        <h3 className="text-2xl font-bold tracking-tight text-[#0D9488] md:text-3xl">More Name Inspiration</h3>
        <Link to="/HomePage">
          <Button variant="ghost" className="hidden text-[#FF6B35] hover:text-[#D8A7CA] sm:flex">
            Explore All Categories <ArrowRight className="ml-2 size-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsList currentPostId={currentPostId} />
      </PostsProvider>
      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="/HomePage">
          <Button variant="outline" className="w-full border-[#FF6B35] text-[#FF6B35] rounded-none">
            Explore All Categories
          </Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
        <div className="space-y-4">
          <div className="h-8 w-2/3 animate-pulse rounded-none bg-[#A7F3D0]/30" />
          <div className="h-4 w-1/3 animate-pulse rounded-none bg-[#A7F3D0]/30" />
          <div className="mt-8 aspect-video w-full animate-pulse rounded-none bg-[#A7F3D0]/30" />
          <div className="mt-8 space-y-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} data-index={i} className="h-4 w-full animate-pulse rounded-none bg-[#A7F3D0]/30" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold text-[#0D9488]">Error Loading Post</h2>
        <p className="mt-2 text-[#333333]">We couldn't load the post you requested.</p>
        <Link to="/HomePage" className="mt-6">
          <Button className="bg-[#FF6B35] hover:bg-[#D8A7CA] text-white rounded-none">Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-bold text-[#0D9488]">Post Not Found</h2>
        <p className="mt-2 text-[#333333]">The article you are looking for does not exist.</p>
        <Link to="/HomePage" className="mt-6">
          <Button className="bg-[#FF6B35] hover:bg-[#D8A7CA] text-white rounded-none">Browse Our Lists</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map((c) => c.id) || [];

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mx-auto max-w-4xl text-center">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
          {post.categories && post.categories.length > 0 && (
            <span className="rounded-none bg-[#0D9488]/10 px-3 py-1 text-xs font-medium text-[#0D9488]">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center text-xs text-[#333333]/70">
            <Calendar className="mr-1 size-3" />
            {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
          </span>
        </div>

        <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-[#0D9488] sm:text-4xl md:text-5xl lg:text-6xl font-sans">
          {post.title}
        </h1>

        <div className="flex items-center justify-center gap-6 text-sm text-[#333333]/70">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-[#0D9488]/10 rounded-none">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback className="rounded-none"><User className="size-4" /></AvatarFallback>
              </Avatar>
              <span className="font-medium text-[#333333]">{post.author.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="size-4" />
            <span>4 min read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mx-auto mt-10 mb-12 overflow-hidden rounded-none shadow-sm md:mt-14">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg dark:prose-invert prose-headings:text-[#0D9488] prose-headings:font-bold prose-headings:tracking-tight prose-a:text-[#FF6B35] prose-a:hover:text-[#D8A7CA] prose-img:rounded-none max-w-none text-[#333333] font-sans"
          dangerouslySetInnerHTML={{ __html: post.content || "" }} 
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-2 border-t border-[#0D9488]/10 pt-8">
            <Tag className="size-4 text-[#333333]/50" />
            <span className="mr-2 text-sm font-medium text-[#333333]/70">Tags:</span>
            {post.tags.map((tag, i) => (
              <Link key={tag.id} data-index={i} to={tag.link || "#"}>
                <span className="rounded-none bg-[#A7F3D0] px-2.5 py-1 text-xs font-medium text-[#0D9488] transition-colors hover:bg-[#D8A7CA] hover:text-white">
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {}
        <div className="mt-8 flex items-center justify-between border-y border-[#0D9488]/10 py-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[#333333]">Share this list:</span>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" className="size-8 rounded-none border-[#FF6B35] text-[#FF6B35] hover:bg-[#D8A7CA] hover:text-white" aria-label="Share on Twitter">
                <Share2 className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {}
        {post.author && (
          <div className="mt-12 rounded-none bg-[#A7F3D0]/30 p-8">
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
              <Avatar className="size-20 border-2 border-[#FFF7ED] shadow-sm rounded-none">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback className="text-lg rounded-none"><User className="size-8" /></AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-[#0D9488]">About {post.author.name}</h3>
                <p className="text-sm text-[#333333]">
                  Helping parents find the perfect name for their little one with care, research, and a passion for global etymology.
                </p>
                <Link to={post.author.link || "#"}>
                  <Button variant="link" className="h-auto p-0 text-[#FF6B35] hover:text-[#D8A7CA]">
                    More from {post.author.name}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {}
      <Separator className="my-16 bg-[#0D9488]/10" />
      <RelatedPostsSection currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}

export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-[#FFF7ED] w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}